/**
 * customerResource — the Shopify customer list, authored against the
 * resource-kit v2 model/view contracts and consumed directly as a
 * `ResourceBundle` by `ResourcePage` (#1805 slice A4a). The
 * three bespoke cell projections moved to `./widgets.tsx` as named widgets;
 * `fields[].render` has no legacy carrier on the v2 side.
 *
 * Two members did NOT survive the move as declarations, and both omissions are
 * load-bearing rather than tidy:
 *
 *   `currencyCode: 'USD'` on `total_spent` — `ModelField` has no slot for it,
 *   and `currencyField`, the slot that DOES exist, is refused outright by the
 *   bridge (`UNRENDERABLE_FIELD_PROPS`). Both renderers already default to
 *   `'USD'` (`packages/resource-kit-ui/react/registry/field-type-registry.tsx::CurrencyDisplay`
 *   and `packages/resource-kit-ui/react/registry/field-type-registry.tsx::FIELD_TYPE_REGISTRY.currency`), so declaring
 *   nothing is byte-identical — proved by the cell + CSV assertions in
 *   `__tests__/customerResource.v2-parity.test.tsx`, not assumed.
 *
 *   `EntityDefinition`/`ResourceDefinition` split — the v2 face has one model
 *   and two views instead, so the old `customerSchema` export is gone. Its only
 *   consumers were this feature's own tests.
 */
import {
  type EnumOption,
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
} from '@resource-kit/model';
import type { ResourceBundle } from '@resource-kit/controllers/react';
import type { Customer, CustomerOrderSummary } from '@/services';
import { customerAPI, toListResult, CUSTOMER_FILTERS } from '@/services';
import { CUSTOMER_SORTS } from '@/services/filters.generated';
import { PERMISSIONS } from '@/permissions';
import i18n from '@/i18n';
import { CustomerOrdersCard } from './components/CustomerOrdersCard';

const stateOptions: EnumOption[] = [
  { value: 'enabled', label: 'customer.enum.enabled', color: 'success' },
  { value: 'disabled', label: 'customer.enum.disabled', color: 'secondary' },
  { value: 'invited', label: 'customer.enum.invited', color: 'primary' },
  { value: 'declined', label: 'customer.enum.declined', color: 'destructive' },
];

const f = createFieldConstructors<Customer>();

export const customerModel = defineModel<Customer>()({
  name: 'customer',
  idField: 'shopify_id',
  // No runtime consumer on this surface: the detail drawer takes its title from
  // `host.detail.title`, and `recName` is read only where this model is a
  // relation TARGET (`features/relations.ts` → `useReference`), which today
  // registers `supplier` alone. Declared because `ModelDef` requires it, and
  // `email` is the one column that names a customer on every row (`first_name`
  // / `last_name` are both nullable).
  recName: 'email',
  // #1405: the former `verified_email`, `last_order_name`, `last_order_id`,
  // `currency`, `accepts_marketing`, `tax_exempt`, `source` and `received_at`
  // columns are gone — the `/api/customers` wire (generated `Customer`,
  // customer-routes `toCustomer`) never emitted them, so they always rendered
  // `undefined` / a dash.
  fields: {
    shopify_id: f.id({ label: 'customer.field.shopifyId' }),
    email: f.email({ label: 'customer.field.email', widget: 'customerEmail' }),
    first_name: f.text({ label: 'customer.field.firstName' }),
    last_name: f.text({ label: 'customer.field.lastName' }),
    // ~1209 customers have a NULL `phone` column while the address carries a
    // phone; the widget falls back to the selected address's phone (list +
    // detail), exactly as the former `render` did.
    phone: f.phone({ label: 'customer.field.phone', widget: 'customerPhone' }),
    orders_count: f.number({ label: 'customer.field.ordersCount' }),
    // No `currencyField` — see the file header. A DECLARED one fails the bridge.
    total_spent: f.currency({ label: 'customer.field.totalSpent' }),
    created_at: f.datetime({ label: 'customer.field.createdAt' }),
    state: f.enum({ label: 'customer.field.state', options: stateOptions }),
    addresses: f.json({ label: 'customer.field.addresses', widget: 'customerAddresses' }),
    tags: f.text({ label: 'customer.field.tags' }),
    note: f.text({ label: 'customer.field.note' }),
  },
  // The row of the backend's data-change ledger this resource writes to. Its
  // only reader is the `ledgerId` gate in
  // `packages/resource-kit-ui/react/form/FormView.tsx`, which derives the
  // change-history tab from it; omitting it removes that whole tab.
  auditResourceType: 'customers',
  // Same split as products (#1963): `idField` is the Shopify id this customer
  // is addressed by, while the ledger keys its rows on the internal UUID
  // primary key. Omitting this queried a key space the writers never used, and
  // an empty ledger response reads as "never touched" rather than as an error.
  // `id` rides the wire already (`backend/src/contexts/commerce/http/customer-routes.ts::customerSchema`).
  auditResourceIdField: 'id',
});

// The ONE ordered field sequence drives both table and detail columns, so every
// modifier belongs here and the form view below only re-groups the names.
export const customerListView = defineListView(customerModel, {
  items: [
    { field: 'shopify_id', columnInvisible: true, invisible: true },
    { field: 'email' },
    { field: 'first_name' },
    { field: 'last_name' },
    { field: 'phone' },
    { field: 'orders_count' },
    { field: 'total_spent' },
    { field: 'created_at' },
    { field: 'state' },
    { field: 'addresses' },
    { field: 'tags', columnInvisible: true },
    { field: 'note', columnInvisible: true },
  ],
  // `/api/customers` really searches (email / first / last name / phone) — see
  // CUSTOMER_SEARCH in filters.generated.ts (#1642), bound in BOTH directions
  // by `services/__tests__/resource-searchable-parity.test.ts`. Turning this
  // off would 400 every search the operator types.
  capabilities: { search: true },
  defaultSort: { field: 'created_at', dir: 'desc' },
});

export const customerFormView = defineFormView(customerModel, {
  groupDisplay: 'tab',
  children: [
    // #1922: `addresses` used to be a fields group of its own. Since #1918 declared
    // the `'tab'` group display, a group is a rail trigger, and a
    // trigger that opens onto one field is pure noise. An address IS a way to
    // reach the customer, so it joins `contact` at the tail — the widget still
    // renders the full address block, only the trigger is gone.
    {
      kind: 'fields',
      id: 'contact',
      label: 'customer.group.contact',
      items: [
        { field: 'email' },
        { field: 'phone' },
        { field: 'first_name' },
        { field: 'last_name' },
        { field: 'addresses' },
      ],
    },
    {
      kind: 'fields',
      id: 'stats',
      label: 'customer.group.stats',
      items: [
        { field: 'orders_count' },
        { field: 'total_spent' },
        { field: 'state' },
        { field: 'created_at' },
      ],
    },
    {
      kind: 'fields',
      id: 'meta',
      label: 'customer.group.meta',
      items: [{ field: 'tags' }, { field: 'note' }, { field: 'shopify_id' }],
    },
    // PR-37 (US-CUSTOMER-002): the detail envelope carries `orders[]` inline, so
    // the recent-orders list renders from the already-fetched payload instead of
    // firing a follow-up `/orders?customer_id=`.
    {
      kind: 'custom',
      id: 'orders',
      label: 'customer.orders.sectionTitle',
      render: function CustomerOrdersTab({ record }) {
        return <CustomerOrdersCard customer={record} />;
      },
    },
    // Declared, not derived (#2783): the kit no longer appends a history tab.
    { kind: 'audit', id: 'audit', label: 'resource.changeHistory' },
  ],
});

export const customerResource: ResourceBundle<Customer, typeof customerModel.fields> = {
  model: customerModel,
  list: customerListView,
  form: customerFormView,
  host: {
    api: {
      // The `/api` `{ items, total, page, size }` page is renamed to the
      // controllers' `ListResult` at this boundary (#2167 PR-2); `size` is the
      // kernel's key and stops here.
      list: async (params) => toListResult(await customerAPI.getCustomers(params)),
      // Flatten the nested `{ customer, orders, orders_truncated }` detail
      // into the flat `Customer` the shared ResourceDetailPanel renders.
      // The inline orders preview keys its link on `shopify_id`; map it to
      // the `shopify_order_id` field `CustomerOrdersCard` reads.
      get: async (id) => {
        const detail = await customerAPI.getCustomer(id);
        const orders: CustomerOrderSummary[] = detail.orders.map((o) => ({
          id: o.id,
          shopify_order_id: o.shopify_id,
          name: o.name,
          order_number: o.order_number,
          total_price: o.total_price,
          financial_status: o.financial_status,
          fulfillment_status: o.fulfillment_status,
          created_at: o.created_at,
        }));
        return {
          ...detail.customer,
          orders,
          orders_truncated: detail.orders_truncated,
        } satisfies Customer;
      },
    },
    label: 'customer.label',
    slug: 'customers',
    listTitle: 'customer.listTitle',
    listDescription: 'customer.listDescription',
    // `customers:list` folded into `customers:read` (#943 catalog unify).
    permission: { list: PERMISSIONS.CUSTOMERS_READ, read: PERMISSIONS.CUSTOMERS_READ },
    defaultConditions: [],
    // Cross-checked by ResourcePage so any active condition the backend would
    // drop surfaces a visible warning instead of a false filtered view (#1220,
    // mechanism from #1154). Mirrors the backend customerListConfig.filterable.
    filterable: CUSTOMER_FILTERS,
    sortable: CUSTOMER_SORTS,
    searchPlaceholder: 'customer.searchPlaceholder',
    staleTime: 5 * 60 * 1000,
    detail: {
      title: (c) =>
        [c.first_name, c.last_name].filter(Boolean).join(' ') ||
        i18n.t('customer.detail.fallbackName'),
      subtitle: (c) => i18n.t('customer.detail.subtitle', { id: c.shopify_id }),
    },
  },
};
